import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const schema = z.object({
  siteUrl: z
    .string()
    .min(1, "Site adresini yazın")
    .refine(
      (value) => {
        const normalized = value.startsWith("http")
          ? value
          : `https://${value}`;
        try {
          new URL(normalized);
          return true;
        } catch {
          return false;
        }
      },
      { message: "Geçerli bir URL girin (örn: 33n.org)" },
    ),
  pluginName: z.string().optional(),
  topGoal: z.string().min(1, "En önemli hedefi seçin").max(80, "Çok uzun"),
  mustHave: z
    .string()
    .min(10, "En az 10 karakter yazın")
    .max(900, "900 karakteri aşmayın"),
});

type DiscoveryValues = z.infer<typeof schema>;

export function DiscoveryForm({ defaultSiteUrl }: { defaultSiteUrl?: string }) {
  const form = useForm<DiscoveryValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      siteUrl: defaultSiteUrl ?? "33n.org",
      pluginName: "",
      topGoal: "Satışa yönlendirme",
      mustHave:
        "Plugin ile eklemek istediğim özellikler ve kullanıcı akışları...",
    },
    mode: "onChange",
  });

  const onSubmit = (values: DiscoveryValues) => {
    toast.success("Not aldım. Sonraki adım: entegrasyon ve teklif planı.", {
      description: `${values.siteUrl} • ${values.topGoal}`,
    });
    form.reset(values);
  };

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className="grid gap-4"
        noValidate
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField
            control={form.control}
            name="siteUrl"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Mevcut site adresi</FormLabel>
                <FormControl>
                  <Input placeholder="33n.org" {...field} />
                </FormControl>
                <FormDescription>
                  WordPress yönetimi sizde kalacaksa da aynı adresi kullanırız.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="pluginName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Plugin adı (varsa)</FormLabel>
                <FormControl>
                  <Input
                    placeholder="Örn: ödeme, üyelik, randevu..."
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Plugin tam adı/URL paylaşabilirseniz entegrasyon daha net
                  olur.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="topGoal"
          render={({ field }) => (
            <FormItem>
              <FormLabel>En önemli hedef</FormLabel>
              <FormControl>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[
                    "Satışa yönlendirme",
                    "Lead toplama",
                    "Premium görünüm",
                    "Plugin kapsamını büyütme",
                  ].map((option) => (
                    <button
                      type="button"
                      key={option}
                      onClick={() => field.onChange(option)}
                      className={
                        option === field.value
                          ? "rounded-xl border border-primary bg-primary/10 px-3 py-2 text-left text-sm font-medium"
                          : "rounded-xl border bg-background px-3 py-2 text-left text-sm text-muted-foreground hover:text-foreground"
                      }
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </FormControl>
              <FormDescription>
                Tasarım + içerik kararlarını bu hedefe göre veririz.
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="mustHave"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Mutlaka olsun dediğiniz şeyler</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Örn: numeroloji raporu satın alma akışı, üyelik seviyeleri, plugin ile yeni alanlar..."
                  className="min-h-[120px]"
                  {...field}
                />
              </FormControl>
              <FormDescription>
                İsterseniz madde madde yazın (özellik + ekran + ödeme kuralı).
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-muted-foreground">
            Gönderince ekranda onay mesajı görürsünüz (şimdilik sadece taslak).
          </div>
          <Button type="submit" className="rounded-full">
            Notları Kaydet
          </Button>
        </div>
      </form>
    </Form>
  );
}
